import { JSX, useState } from "react";
import { logger } from "../helpers/tslog";
import { Tooltip } from "antd";
import { InfoCircleOutlined } from "@ant-design/icons";
import { httpClient } from "./api/client";

interface InputItem {
  symbol: string;
  name: string;
  exchangeCode: string;
  searchableName: string;
  category: string;
  type: string;
  country: string;
  currency: string;
  isin: string;
}

type InputData = {
  [key: string]: {
    metadata: {
      label: string;
      description: string;
    };
    tickers: InputItem[];
  };
};

function transformOptions(data: InputData, search: string) {
  return Object.entries(data).map(([, categoryData]) => {
    return {
      label: (
        <div style={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
          <span style={{ marginRight: "10px" }}>{categoryData.metadata.label}</span>
          <Tooltip placement="top" title={categoryData.metadata.description}>
            <div
              style={{
                cursor: "pointer",
              }}
            >
              <InfoCircleOutlined style={{ cursor: "pointer" }} />
            </div>
          </Tooltip>
        </div>
      ),
      options: categoryData.tickers.map((item) => ({
        label: (
          <div style={{ display: "flex", justifyContent: "space-between" }}>
            <span>{highlightText(item.name, search)}</span>
            <span>{highlightText(item.symbol, search, true)}</span>
          </div>
        ),
        value: JSON.stringify(item),
      })),
    };
  });
}

const highlightText = (text: string, searchQuery: string, isSymbol: boolean = false) => {
  if (!searchQuery) return text;

  // Split search query into individual words and filter out empty strings
  const searchWords = searchQuery
    .toLowerCase()
    .split(/\s+/)
    .filter((word) => word.length > 0);

  if (isSymbol) {
    // Split into exchange and ticker
    const [exchange, ticker] = text.split(":");
    if (!ticker) return text; // If no colon found, return as is

    // Only highlight the ticker part
    const words = ticker.split(/\s+/);
    const highlightedTicker = words
      .map((word, i) => {
        const wordLower = word.toLowerCase();
        // Check if any search word is contained in this word
        const shouldHighlight = searchWords.some((searchWord) => wordLower.includes(searchWord));
        if (shouldHighlight) {
          return <strong key={i}>{word}</strong>;
        }
        return word;
      })
      .reduce(
        (prev, curr, i) => {
          return i === 0 ? [curr] : [...prev, " ", curr];
        },
        [] as (string | JSX.Element)[]
      );

    return (
      <>
        {exchange}:{highlightedTicker}
      </>
    );
  }

  // Regular text highlighting
  const words = text.split(/\s+/);
  return words
    .map((word, i) => {
      const wordLower = word.toLowerCase();
      // Check if any search word is contained in this word
      const shouldHighlight = searchWords.some((searchWord) => wordLower.includes(searchWord));
      if (shouldHighlight) {
        return <strong key={i}>{word}</strong>;
      }
      return word;
    })
    .reduce(
      (prev, curr, i) => {
        return i === 0 ? [curr] : [...prev, " ", curr];
      },
      [] as (string | JSX.Element)[]
    );
};

export default function useFetchCompanies(searchQuery: string = "") {
  const [companyOptions, setCompanyOptions] = useState<any>([]);

  async function fetchCompanies(search: string = searchQuery) {
    try {
      const { data } = await httpClient.get("/search/company", {
        params: {
          search,
          limit: 10,
        },
        withCredentials: true,
      });

      setCompanyOptions(transformOptions(data, search));
    } catch (err) {
      logger.error("Error fetching companies:", err);
    }
  }

  return { companyOptions, fetchCompanies };
}
